import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RevealText } from "@/components/RevealText";
import { submitContact } from "@/lib/api";
import { CONTACT, SERVICE_OPTIONS } from "@/lib/constants";

const initial = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await submitContact(form);
      setSent(true);
      setForm(initial);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 md:pt-44" data-testid="contact-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 border-b border-white/10">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-8 flex items-center gap-3">
          <span className="w-8 h-px bg-[color:var(--accent)]" /> Contact
        </p>
        <h1 className="font-display text-6xl md:text-[9vw] uppercase tracking-tighter leading-[0.82]">
          <RevealText lines={["Let's build"]} />
          <span className="block text-white/25"><RevealText lines={["something."]} delay={0.12} /></span>
        </h1>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Details */}
        <div className="lg:col-span-4 space-y-12">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-5">Direct</p>
            <a href={`mailto:${CONTACT.email}`} className="group flex items-start gap-4 mb-6" data-testid="contact-email-link">
              <Mail size={18} className="text-[color:var(--accent)] mt-1" />
              <span className="font-mono text-sm text-white/80 group-hover:text-white transition-colors break-all">{CONTACT.email}</span>
            </a>
            {CONTACT.phonesRaw.map((raw, i) => (
              <a key={raw} href={`tel:${raw}`} className="group flex items-center gap-4 mb-4" data-testid={`contact-phone-link-${i}`}>
                <Phone size={18} className="text-[color:var(--accent)]" />
                <span className="font-mono text-sm text-white/80 group-hover:text-white transition-colors">{CONTACT.phones[i]}</span>
              </a>
            ))}
          </div>
          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-5">Studio</p>
            <div className="flex items-start gap-4">
              <MapPin size={18} className="text-[color:var(--accent)] mt-1" />
              <address className="font-mono text-sm text-white/70 not-italic leading-relaxed" data-testid="contact-address">
                {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
              </address>
            </div>
          </div>
          <div className="border border-white/10 aspect-[4/3] overflow-hidden">
            <img src="https://images.unsplash.com/photo-1771879808903-bfd5208ef6f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Studio" className="w-full h-full object-cover grayscale" />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7 lg:col-start-6">
          {sent ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/15 p-10 md:p-16" data-testid="contact-success">
              <span className="w-3 h-3 bg-[color:var(--accent)] inline-block mb-8" />
              <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-4">Message received.</h2>
              <p className="font-mono text-sm text-white/55 max-w-md mb-8">Thanks for reaching out. A member of our team will get back to you at the email you provided.</p>
              <button onClick={() => setSent(false)} className="font-mono text-xs tracking-[0.2em] uppercase border border-white/20 px-6 py-4 hover:bg-white hover:text-black transition-colors" data-testid="contact-send-another">
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <Field label="Your name *" value={form.name} onChange={set("name")} placeholder="Jane Doe" testid="contact-name" />
                <Field label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" testid="contact-email" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <Field label="Phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" testid="contact-phone" />
                <Field label="Company" value={form.company} onChange={set("company")} placeholder="Company name" testid="contact-company" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3 block">Service</label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 h-auto py-3 font-mono text-white focus:ring-0 focus:border-white" data-testid="contact-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[color:var(--surface)] border-white/15 font-mono">
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} className="rounded-none focus:bg-white/10">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div />
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3 block">Tell us about your project *</label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="What are you building?"
                  className="w-full bg-transparent border-b border-white/20 focus:border-white focus:outline-none placeholder-white/30 font-mono py-3 resize-none text-white"
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="group inline-flex items-center gap-3 bg-white text-black px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors disabled:opacity-50"
                data-testid="contact-submit"
              >
                {busy ? "Sending…" : "Send message"}
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/20 focus:border-white focus:outline-none placeholder-white/30 font-mono py-3 text-white"
        data-testid={testid}
      />
    </div>
  );
}
